import React, { Fragment, memo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SkipStep } from 'src/constants/actionTypes'
import { CommonModal } from '../shared/CommonModal'
import { Button } from '../shared/Button'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import BGCIcon from 'src/assets/icons/bgc.svg'
import { handleApiError } from 'src/utils/errorHandlers'
import { BGCOption, BGCOptionType } from './types'
import { useYardstik } from 'src/hooks/useYardstik'
import { BGCCollectDataStep } from 'src/interfaces/collectData'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { SecureWrapper } from '../layout/SecureWrapper'
import { StepLabels } from '../client/step-labels/StepLabels'

const buttonText: Record<BGCOptionType, string> = {
  yardstik: 'Continue with Yardstik',
  manual: 'Continue with Manual Upload',
  skip: 'Continue without BC & MVR',
}

interface Props {
  options: BGCOption[]
  selectedOption: BGCOptionType | undefined
  onSelect: (value: BGCOptionType | undefined) => void
  onBack?: () => void
}
const BGCSelectOptionComponent = ({ options, selectedOption, onSelect, onBack }: Props) => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as BGCCollectDataStep

  const [selectedRadioType, setSelectedRadioType] = useState<BGCOptionType | undefined>(undefined)

  const handleSkip = useCallback(async () => {
    const requestBody = {
      stepId: currentStep.id,
    }

    try {
      await createAsyncAction(dispatch, SkipStep.request(requestBody))
    } catch (err: any) {
      handleApiError(err)
    }
  }, [currentStep.id])

  const handleCloseModal = useCallback(() => {
    onSelect(undefined)
  }, [onSelect])

  const { isYardstikLoading, startYardstik, YardstikView } = useYardstik()

  const showYardstik = useCallback(() => {
    startYardstik(currentStep.id)
  }, [currentStep.id])

  return (
    <Fragment>
      <SecureWrapper
        onBack={onBack}
        footer={
          selectedRadioType ? (
            <Button
              className={tw`w-full`}
              loading={isYardstikLoading}
              onClick={() => {
                if (selectedRadioType === 'yardstik') {
                  showYardstik()

                  return
                }

                onSelect(selectedRadioType)
              }}
              data-testid="contractors-onboarding-bgc-continue-btn"
            >
              {buttonText[selectedRadioType]}
            </Button>
          ) : undefined
        }
      >
        <div
          css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
        >
          <img src={BGCIcon} />
        </div>
        <StepLabels />
        <Typography css={tw`mb-2 mt-2`} variant="h2">
          Upload Background Check & Motor Vehicle Record
        </Typography>
        <Typography css={tw`mb-4 text-shades-400`}>
          Once you submit the documents, you will automatically advance to the next step.
        </Typography>
        <Typography css={tw`text-shades-400`} variant="overline">
          SELECT AN OPTION
        </Typography>
        <div css={tw`rounded-2xl p-4 pt-2 bg-shades-0 mb-6 mt-2`}>
          <BasicRadioGroup
            value={selectedRadioType}
            options={options}
            onChange={(_, value) => {
              setSelectedRadioType(value as BGCOptionType)
            }}
            dataTestId="contractors-onboarding-bgc-option-input"
          />
        </div>
        {selectedOption === 'skip' && (
          <CommonModal
            isOpen
            title="Continue without Background Check & Motor Vehicle Record?"
            text="Are you sure you want to proceed without Background Check & Motor Vehicle Record? You won't be able to return to this step once you continue."
            className={tw`w-[358px]`}
            buttons={
              <div css={tw`w-full gap-2 flex flex-col`}>
                <Button
                  className="w-full"
                  onClick={handleSkip}
                  data-testid="contractors-onboarding-bgc-skip-confirm-btn"
                >
                  Skip the step
                </Button>
                <Button
                  color="grey"
                  onClick={handleCloseModal}
                  data-testid="contractors-onboarding-bgc-skip-back-btn"
                >
                  Back
                </Button>
              </div>
            }
            onClose={handleCloseModal}
          />
        )}
      </SecureWrapper>
      <YardstikView />
    </Fragment>
  )
}

export const BGCSelectOption = memo(BGCSelectOptionComponent)
